import { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Clock, Coins, User, MapPin, Wallet, StickyNote } from "lucide-react";

const empty = {
  name: "", description: "",
  default_price: 0, default_duration_minutes: 60, active: true,
  default_staff_id: "", default_payment_type_code: "",
  default_location: { postcode: "", address_line: "" },
  default_notes: "",
};

function TemplateDialog({ open, onOpenChange, initial, onSaved, staff, paymentTypes }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || empty);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        ...empty,
        ...initial,
        default_staff_id: initial.default_staff_id || "",
        default_payment_type_code: initial.default_payment_type_code || "",
        default_location: initial.default_location || { postcode: "", address_line: "" },
        default_notes: initial.default_notes || "",
      });
    } else {
      setForm(empty);
    }
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const loc = form.default_location || {};
      const hasLoc = !!(loc.postcode?.trim() || loc.address_line?.trim());
      const payload = {
        name: form.name,
        description: form.description,
        default_price: Number(form.default_price),
        default_duration_minutes: Number(form.default_duration_minutes),
        active: !!form.active,
        default_staff_id: form.default_staff_id || null,
        default_payment_type_code: form.default_payment_type_code || null,
        default_location: hasLoc ? {
          postcode: loc.postcode || null,
          address_line: loc.address_line || null,
        } : null,
        default_notes: form.default_notes || "",
      };
      if (isEdit) {
        await api.put(`/templates/${initial.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates", payload);
        toast.success("Template created");
      }
      onSaved();
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription>
            Defaults are pre-filled into the booking form when this template is selected.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="template-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="template-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Default price</Label>
              <Input type="number" step="0.01" min="0" value={form.default_price}
                     onChange={(e) => setForm({ ...form, default_price: e.target.value })} data-testid="template-price-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Duration (min)</Label>
              <Input type="number" step="1" min="1" value={form.default_duration_minutes}
                     onChange={(e) => setForm({ ...form, default_duration_minutes: e.target.value })} data-testid="template-duration-input" />
            </div>
          </div>

          <div className="border-t border-border pt-4 space-y-4">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-accent">// Booking defaults (optional)</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Default staff</Label>
                <Select
                  value={form.default_staff_id || "_none"}
                  onValueChange={(v) => setForm({ ...form, default_staff_id: v === "_none" ? "" : v })}
                >
                  <SelectTrigger data-testid="template-staff-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" data-testid="template-staff-none">None</SelectItem>
                    {(staff || []).filter((s) => s.active).map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`template-staff-${s.id}`}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Default payment type</Label>
                <Select
                  value={form.default_payment_type_code || "_none"}
                  onValueChange={(v) => setForm({ ...form, default_payment_type_code: v === "_none" ? "" : v })}
                >
                  <SelectTrigger data-testid="template-pt-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none" data-testid="template-pt-none">None / use customer or system default</SelectItem>
                    {(paymentTypes || []).filter((p) => !p.archived).map((p) => (
                      <SelectItem key={p.id} value={p.code} data-testid={`template-pt-${p.code}`}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Default postcode</Label>
                <Input
                  value={form.default_location?.postcode || ""}
                  onChange={(e) => setForm({ ...form, default_location: { ...form.default_location, postcode: e.target.value } })}
                  data-testid="template-location-postcode"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold">Default address line</Label>
                <Input
                  value={form.default_location?.address_line || ""}
                  onChange={(e) => setForm({ ...form, default_location: { ...form.default_location, address_line: e.target.value } })}
                  data-testid="template-location-address"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Default notes</Label>
              <Textarea
                rows={2}
                value={form.default_notes || ""}
                onChange={(e) => setForm({ ...form, default_notes: e.target.value })}
                data-testid="template-default-notes"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="template-active-switch" />
            <Label className="text-sm">Active</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="template-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="template-save-button">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("EUR");
  const [staff, setStaff] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [tplRes, setRes, staffRes, ptRes] = await Promise.all([
        api.get("/templates"),
        api.get("/settings").catch(() => ({ data: { currency: "EUR" } })),
        api.get("/staff").catch(() => ({ data: [] })),
        api.get("/settings/payment-types").catch(() => ({ data: [] })),
      ]);
      setItems(tplRes.data);
      setCurrency(setRes.data?.currency || "EUR");
      setStaff(staffRes.data || []);
      setPaymentTypes(ptRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const staffName = (id) => staff.find((s) => s.id === id)?.name || "";
  const ptName = (code) => paymentTypes.find((p) => p.code === code)?.name || code;

  return (
    <div className="page" data-testid="templates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manage</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Job Templates</h1>
        </div>
        <Button className="btn-cta gap-2" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-template-button">
          <Plus className="h-4 w-4" /> New template
        </Button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center text-muted-foreground text-sm">
            No templates yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-[8px] p-5" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-base truncate">{t.name}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                  </div>
                  {!t.active && (
                    <span className="mono text-[10px] uppercase tracking-widest bg-muted px-2 py-0.5 rounded shrink-0">Inactive</span>
                  )}
                </div>
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <span className="flex items-center gap-1 font-bold"><Coins className="h-4 w-4" />{formatCurrency(t.default_price, currency)}</span>
                  <span className="flex items-center gap-1 text-muted-foreground"><Clock className="h-4 w-4" />{t.default_duration_minutes}m</span>
                </div>
                {/* Phase 6 default chips */}
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`template-defaults-${t.id}`}>
                  {t.default_staff_id && (
                    <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded">
                      <User className="h-3 w-3" /> {staffName(t.default_staff_id) || "—"}
                    </span>
                  )}
                  {t.default_payment_type_code && (
                    <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded">
                      <Wallet className="h-3 w-3" /> {ptName(t.default_payment_type_code)}
                    </span>
                  )}
                  {t.default_location?.postcode && (
                    <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded">
                      <MapPin className="h-3 w-3" /> {t.default_location.postcode}
                    </span>
                  )}
                  {t.default_notes && (
                    <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded" title={t.default_notes}>
                      <StickyNote className="h-3 w-3" /> notes
                    </span>
                  )}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`edit-template-${t.id}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-template-${t.id}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{t.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onDelete(t.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-template-${t.id}`}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TemplateDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} staff={staff} paymentTypes={paymentTypes} />
    </div>
  );
}
